import { CheckCircle2, KeyRound, RefreshCw, TriangleAlert } from 'lucide-react'
import { useId, useState } from 'react'

import { Button, DiagnosticCopy, StatusMessage } from '@krp/ui-kit'

import type { OneCWebhookActions, OneCWebhookState, OneCWebhookVm } from '../model/types'
import { WEBHOOK_CHECKLIST } from '../model/webhookView'
import '../../../shared/ui/onec.css'

const STATE_ICON: Record<OneCWebhookState, typeof CheckCircle2> = {
  receiving: CheckCircle2,
  stale: TriangleAlert,
  'never-received': TriangleAlert,
  'no-secret': KeyRound,
}

/**
 * Вебхук 1С в карточке базы: адрес для константы расширения, состояние доставки и смена секрета.
 * Справка ⓘ экрана одна и остаётся за карточкой ресурса — здесь объяснение обычными словами в тексте.
 * Секрет показывается один раз, крупно; сервер хранит только его хеш.
 */
export function OneCWebhookPanel({ vm, actions }: { vm: OneCWebhookVm; actions: OneCWebhookActions }) {
  const headingId = useId()
  const [confirming, setConfirming] = useState(false)
  const { delivery, query } = vm
  const Icon = delivery === null ? KeyRound : STATE_ICON[delivery.state]

  return (
    <section className="onec-webhook" aria-labelledby={headingId}>
      <h3 id={headingId}>Вебхук 1С</h3>
      <p>
        1С сама сообщает сюда, что товары изменились, — так цены и остатки обновляются без ручной проверки. Для этого в расширении 1С нужны адрес
        и секрет.
      </p>

      {query.status === 'loading' || query.status === 'idle' ? (
        <StatusMessage kind="loading" title="Состояние вебхука загружается…" />
      ) : delivery === null ? (
        <StatusMessage
          kind={query.status === 'forbidden' ? 'forbidden' : query.status === 'unavailable' ? 'unavailable' : 'error'}
          title={query.status === 'forbidden' ? 'Нет права управлять вебхуком этой базы' : 'Состояние вебхука не получено'}
          text={query.error?.message}
          {...(query.error !== null && query.error.correlationId !== null
            ? { meta: `Код: ${query.error.code} · запрос ${query.error.correlationId}` }
            : {})}
          action={
            <Button variant="secondary" icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.reload}>
              Повторить
            </Button>
          }
        />
      ) : (
        <>
          <p role="status" className="onec-webhook__state" data-state={delivery.state}>
            <Icon size={18} aria-hidden="true" /> <strong>{vm.stateTitle}</strong>
          </p>
          <dl className="onec-webhook__facts">
            <dt>Последний пакет</dt>
            <dd>{vm.lastBatchText}</dd>
            <dt>За сутки</dt>
            <dd>{vm.dayText}</dd>
            <dt>Ждут обработки</dt>
            <dd>{vm.pendingText}</dd>
          </dl>
          {vm.rejectionText !== null && (
            <p role="alert" className="onec-webhook__problem">
              <TriangleAlert size={16} aria-hidden="true" /> {vm.rejectionText}
            </p>
          )}
          {vm.failureText !== null && (
            <p role="alert" className="onec-webhook__problem">
              <TriangleAlert size={16} aria-hidden="true" /> {vm.failureText}
            </p>
          )}
          {vm.attention.text !== null && (
            <div role="alert" className="onec-webhook__problem">
              <p>
                <TriangleAlert size={16} aria-hidden="true" /> {vm.attention.text}
              </p>
              {vm.attention.canRetry && (
                <Button
                  variant="secondary"
                  icon={<RefreshCw size={16} aria-hidden="true" />}
                  busy={vm.attention.busy}
                  onClick={actions.retryAttention}
                >
                  Повторить
                </Button>
              )}
            </div>
          )}
          {vm.attention.done !== null && (
            <p role="status">
              <CheckCircle2 size={16} aria-hidden="true" /> {vm.attention.done}
            </p>
          )}
          {vm.attention.error !== null && (
            <StatusMessage
              kind="error"
              title="Пакеты не возвращены в очередь"
              text={vm.attention.error.message}
              meta={`Код: ${vm.attention.error.code}${vm.attention.error.correlationId !== null ? ` · запрос ${vm.attention.error.correlationId}` : ''}`}
            />
          )}
          {vm.agents.length > 0 && (
            <div role="alert" className="onec-webhook__problem onec-webhook__agents">
              <p>
                <TriangleAlert size={16} aria-hidden="true" /> <strong>Агент рядом с 1С</strong> — служба на сервере 1С, которая принимает
                пакеты от 1С и доставляет их сюда.
              </p>
              {vm.agents.map((agent) => (
                <div key={agent.key} className="onec-webhook__agent">
                  <p>
                    <strong>{agent.title}</strong>
                  </p>
                  <ul>
                    {agent.lines.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                  <DiagnosticCopy text={agent.copyText} label="Копировать для администратора" />
                </div>
              ))}
            </div>
          )}
          {vm.warning !== null && (
            <div role="alert" className="onec-webhook__warning">
              <p>
                <TriangleAlert size={16} aria-hidden="true" /> <strong>{vm.warning.title}.</strong> Проверьте в 1С:
              </p>
              <ul>
                {WEBHOOK_CHECKLIST.map((item) => (
                  <li key={item.name}>
                    <strong>{item.name}</strong> — {item.detail}
                  </li>
                ))}
              </ul>
              <small>
                Если 1С присылает неверный секрет, служба отвечает ей «401», но не может понять, чья это база, поэтому здесь такой отказ не виден.
                Когда пакетов нет вовсе, начните с адреса и секрета.
              </small>
            </div>
          )}

          <div className="onec-webhook__address">
            <span className="onec-webhook__label">Адрес приёма (в константу «Адрес вебхука»)</span>
            {delivery.address === null ? (
              <p role="status">
                <TriangleAlert size={16} aria-hidden="true" /> Публичный адрес сервера не настроен в установке, поэтому адрес показать нельзя.
                Администратору: задайте OneC__Webhook__PublicBaseUrl.
              </p>
            ) : (
              <>
                <code className="onec-webhook__value">{delivery.address}</code>
                <DiagnosticCopy text={delivery.address} label="Копировать адрес" />
              </>
            )}
          </div>
        </>
      )}

      {vm.secret !== null && (
        <div className="onec-webhook__secret" role="alert">
          <p>
            <KeyRound size={16} aria-hidden="true" /> <strong>Новый секрет. Он показывается один раз.</strong>
          </p>
          <code className="onec-webhook__value onec-webhook__value--secret">{vm.secret.secret}</code>
          <DiagnosticCopy text={vm.secret.secret} label="Копировать секрет" />
          <p>
            В 1С вставьте его в константу «Секрет вебхука». Заголовок запроса — {vm.secret.header}.
            {vm.secret.revoked > 0
              ? ` Прежних секретов отозвано: ${vm.secret.revoked}, они больше не работают.`
              : ' Прежних действующих секретов не было.'}
          </p>
          <Button variant="primary" onClick={actions.dismissSecret}>
            Секрет вставлен в 1С, скрыть
          </Button>
        </div>
      )}

      {vm.rotate.error !== null && (
        <StatusMessage
          kind="error"
          title="Секрет не выпущен"
          text={vm.rotate.error.message}
          meta={`Код: ${vm.rotate.error.code}${vm.rotate.error.correlationId !== null ? ` · запрос ${vm.rotate.error.correlationId}` : ''}`}
          action={
            <Button variant="secondary" icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.rotate}>
              Повторить
            </Button>
          }
        />
      )}

      {delivery !== null &&
        vm.secret === null &&
        (confirming ? (
          <div className="onec-webhook__confirm" role="alertdialog" aria-label="Подтверждение смены секрета">
            <p>
              <TriangleAlert size={16} aria-hidden="true" /> <strong>Выпустить новый секрет?</strong> Прежний секрет перестанет работать сразу.
              Пока вы не вставите новый в 1С, уведомления от неё приниматься не будут.
            </p>
            <div className="onec-webhook__buttons">
              <Button
                variant="danger"
                busy={vm.rotate.busy}
                onClick={() => {
                  setConfirming(false)
                  actions.rotate()
                }}
              >
                Выпустить и отозвать прежний
              </Button>
              <Button variant="secondary" onClick={() => setConfirming(false)}>
                Отмена
              </Button>
            </div>
          </div>
        ) : (
          <Button
            variant="secondary"
            icon={<KeyRound size={16} aria-hidden="true" />}
            busy={vm.rotate.busy}
            onClick={() => setConfirming(true)}
          >
            {delivery.activeSecrets === 0 ? 'Выпустить секрет' : 'Выпустить новый секрет'}
          </Button>
        ))}
    </section>
  )
}
